import client from "../../configs/graphql.config";
import Loading from "../../components/Loading";
import { START_SIGNATURE_PROCESS, RESET_SIGNATURE_PROCESS } from "../../graphql/skribble";
import Avatar2Svg from "../../assets/images/avatar_2.svg";
import { useMutation, useQuery } from "@apollo/client";
import { CalloutMode } from "../../ui_atoms/Callout";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import {
    ContractSignatureStatus,
    ProfileType,
    SkribbleSigningState,
} from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import MarkdownView from "../../components/MarkdownView";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Linking, TextStyle, Platform } from "react-native";
import { useIntercom } from "../../hooks/intercom";
import Button, { ButtonMode } from "../../components/Button";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import WebViewWrapper from "../../components/WebViewWrapper";
import { useTheme } from "../../hooks/use-theme";
import { LiftTheme } from "../../theme";
import Text from "../../components/Text";
import { useTracking } from "../../hooks/tracking";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { GET_CONTRACT } from "../../graphql/core";
import { isCancelEndUrls, isFailEndUrls, isSuccessEndUrls } from "../../skribble/helpers";
import { useSnackbar } from "../../hooks/snackbar";
import { useAssetWaiting } from "../../hooks/assets";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import * as Sentry from "@sentry/react-native";
import { kycGradient } from "../../configs/linear-gradients";
import { BubbleTalk } from "../../components/BubbleTalk";
import { TitleMediumText } from "../../components/FormattedText";
import { useStatusBarTranslucent } from "../../hooks/use-status-bar-translucent";

type ContractSigningScreenProps = StackScreenProps<RootStackParamList, "ContractSigningScreen">;

type MessageInfo = {
    text: string;
    mode: CalloutMode;
};

export type ActionType = "start" | "goHome" | "goMission" | "retry" | "canceled";

const MESSAGE_INFO_STYLE: Record<CalloutMode, (theme: LiftTheme) => TextStyle> = {
    info: (theme) => ({ color: theme.colors.primary }),
    success: (theme) => ({ color: theme.colors.onSuccessBackground }),
    error: (theme) => ({ color: theme.colors.onErrorBackground }),
    warning: (theme) => ({ color: theme.colors.onWarningBackground }),
};

const MessageInfoDisplay: React.FC<{ messageInfo: MessageInfo }> = ({ messageInfo }) => {
    const theme = useTheme();
    return (
        <Text
            style={[
                { ...theme.fonts.bodyMedium, marginVertical: theme.gridSize, textAlign: "center" },
                MESSAGE_INFO_STYLE[messageInfo.mode](theme),
            ]}
        >
            {messageInfo.text}
        </Text>
    );
};

const ContractSigningScreen: React.FC<ContractSigningScreenProps> = ({ route, navigation }) => {
    const i18n = useI18n();
    const currentContractId = useRef<string>();
    const { fonts, colors } = useTheme();
    const { user } = React.useContext(AuthManagerContext);
    const { profileType } = React.useContext(ProfileSwitcherManagerContext);
    const { initialState = null } = route.params;

    // when signed, hold the asset Id to check
    const [waitingAssetId, setWaitingAssetId] = useState<string>();

    // information display to the user
    const [messageInfo, setMessageInfo] = useState<MessageInfo | null>(null); // [message, mode]

    // action available to the user on the bottom button
    const [actionType, setActionType] = useState<ActionType | null>(initialState);

    // current displayed webView
    const [webViewUrl, setWebviewUrl] = useState<string | null>(null);

    // hold the url for signing process
    const [processUrl, setProcessUrl] = useState<string | null>(null);

    const { showSnackbar } = useSnackbar();
    // true while initializeSigning is getting information from the API
    const [initializing, setInitializing] = useState<boolean>(false);
    const { trackEvent } = useTracking();
    const { presentIntercom } = useIntercom();

    // hold the asset when signing is completed
    // DO not pull asset when the webview is active.
    const { asset, waiting } = useAssetWaiting(webViewUrl ? undefined : waitingAssetId);

    const { data } = useQuery(GET_CONTRACT, {
        variables: { id: route.params.contractId },
        pollInterval: messageInfo?.mode === "error" ? 10000 : 0, // 5 seconds, this is usefull when contract is rejected and we are waiting webhook
    });

    const [startSignatureProcess, { error, loading }] = useMutation(START_SIGNATURE_PROCESS);
    const [resetSignatureProcess, { error: resetError, loading: reseting }] =
        useMutation(RESET_SIGNATURE_PROCESS);
    const goToMission = useCallback(() => {
        if (!route.params.contractId || navigation.getState().routes.length === 0) {
            navigation.popToTop();
        } else {
            if (navigation.getState().routes[0].name === "Home") {
                let screenName = "MissionsScreen" as const;
                if (
                    navigation.getState().routes[0].state?.routeNames &&
                    navigation.getState().routes[0].state?.index !== undefined
                ) {
                    screenName =
                        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        navigation.getState().routes[0].state.routeNames[
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            navigation.getState().routes[0].state.index
                        ];
                }
                navigation.popTo("Home", {
                    screen: screenName,
                    params: {
                        screen: "MissionDetails",
                        params: {
                            missionId: route.params.contractId,
                        },
                    },
                });
            } else {
                navigation.popToTop();
            }
        }
    }, [navigation, route.params.contractId]);

    const refreshQueries = useCallback(async () => {
        await client.refetchQueries({
            include: [FIND_MY_MISSIONS, GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO],
        });
    }, []);

    const onSuccess = useCallback(() => {
        const text =
            profileType === ProfileType.Client
                ? i18n.t("screens.contract_signing.signing_success_client")
                : i18n.t("screens.contract_signing.signing_success_worker");
        showSnackbar({
            text,
            mode: "success",
            timeout: 7000,
        });
        void refreshQueries();
        goToMission();
    }, [goToMission, i18n, profileType, showSnackbar, refreshQueries]);

    /**
     * Initialize the signing process, call the API to get or create the signing basket
     * and update state accordingly
     */
    const initializeSigning = useCallback(
        async (contractId: string) => {
            setMessageInfo(null);
            setWebviewUrl(null);
            setActionType("goHome");
            setProcessUrl(null);

            try {
                setInitializing(true);
                const gqlResult = await startSignatureProcess({
                    variables: { contractId },
                    fetchPolicy: "network-only",
                });
                if (gqlResult.data) {
                    const procObj = gqlResult.data.startSignatureProcess;
                    switch (procObj.signState) {
                        case SkribbleSigningState.Declined:
                            setMessageInfo({
                                text: i18n.t(
                                    "screens.contract_signing.state_message.sign_declined",
                                ),
                                mode: "error",
                            });
                            setActionType("goMission");
                            void trackEvent("contract_signing_declined");
                            break;
                        case SkribbleSigningState.Withdrawn:
                            setMessageInfo({
                                text: i18n.t(
                                    "screens.contract_signing.state_message.sign_withdrawn",
                                ),
                                mode: "error",
                            });
                            setActionType("goMission");
                            void trackEvent("contract_signing_withdraw");
                            break;
                        case SkribbleSigningState.Open:
                            if (procObj.signUrl) {
                                setWebviewUrl(procObj.signUrl);
                                setWaitingAssetId(procObj.signedAssetId || undefined);
                            } else if (procObj.emailSent) {
                                setMessageInfo({
                                    text: i18n.t(
                                        "screens.contract_signing.state_message.email_sent",
                                        { email: user?.email },
                                    ),
                                    mode: "info",
                                });
                            } else {
                                // no signUrl and no email sent, this is an error
                                setMessageInfo({
                                    text: i18n.t(
                                        "screens.contract_signing.state_message.sign_error",
                                    ),
                                    mode: "error",
                                });
                                void trackEvent("contract_signing_error");
                                Sentry.captureException(
                                    new Error(
                                        "Contract signing error. No signUrl and no email sent",
                                    ),
                                );
                                setActionType("goMission");
                            }
                            break;
                        case SkribbleSigningState.Signed:
                            setWaitingAssetId(procObj.signedAssetId || undefined);
                            void trackEvent("contract_signing_success");
                            // onSuccess(); will be called when asset is ready
                            break;
                        default:
                            throw new Error("Unknown signing state");
                    }
                } else if (gqlResult.errors?.length) {
                    navigation.navigate("NotFoundScreen", {
                        error: gqlResult.errors?.[0],
                        goBack: true,
                        isNotFound: true,
                    });
                } else {
                    throw new Error("No data and no errors in graphql payload");
                }
            } finally {
                setInitializing(false);
            }
        },
        [i18n, navigation, startSignatureProcess, trackEvent, user?.email],
    );

    useEffect(() => {
        const listener = Linking.addEventListener("url", (e) => {
            // eslint-disable-next-line no-console
            console.log("ContractSigningScreen link url received --> ", e);
            // we receive a link notification, we should remove the webview
            setWebviewUrl(null);
            // we restart the initialization process to get back the state
            // of the signing process and act accordingly
            void initializeSigning(route?.params?.contractId);
        });
        return () => {
            listener.remove();
        };
    }, [initializeSigning, route?.params?.contractId]);

    const resetSigning = useCallback(async () => {
        await resetSignatureProcess({ variables: { contractId: route?.params?.contractId } });
        await initializeSigning(route?.params?.contractId);
    }, [resetSignatureProcess, initializeSigning, route?.params?.contractId]);

    /**
     * Entry point of the screen, if the parameter change, restart the initialization process
     */
    useEffect(() => {
        if (route?.params?.contractId !== currentContractId.current && !initialState) {
            currentContractId.current = route?.params?.contractId;
            void initializeSigning(route?.params?.contractId);
        }
    }, [initialState, initializeSigning, route?.params?.contractId]);

    useEffect(() => {
        if (
            [
                ContractSignatureStatus.CanceledBySelf,
                ContractSignatureStatus.CanceledByLse,
            ].includes(
                data?.getContract?.signatureStatus || ContractSignatureStatus.WaitingContract,
            )
        ) {
            goToMission();
        }
    }, [data?.getContract?.signatureStatus, goToMission]);

    const onBottomAction = useCallback(async () => {
        switch (actionType) {
            case "start":
                setWebviewUrl(processUrl);
                break;
            case "goHome":
                void refreshQueries();
                navigation.reset({
                    index: 0,
                    routes: [{ name: "Home" }],
                });
                break;
            case "goMission":
                void refreshQueries();
                goToMission();
                break;
            case "canceled":
                void refreshQueries();
                navigation.popTo("Home", {
                    screen: "MissionsScreen",
                    params: {
                        screen: "MissionDetails",
                        params: {
                            missionId: route?.params?.contractId,
                        },
                    },
                });
                break;
            case "retry":
                await resetSigning();
                break;
        }
    }, [
        actionType,
        goToMission,
        navigation,
        processUrl,
        refreshQueries,
        resetSigning,
        route?.params?.contractId,
    ]);

    const onBottomActionTwo = useCallback(() => {
        switch (actionType) {
            case "canceled":
                void refreshQueries();
                void initializeSigning(route?.params?.contractId);
                break;
        }
    }, [actionType, initializeSigning, refreshQueries, route?.params?.contractId]);

    const [buttonMode, buttonTitle]: [ButtonMode?, string?] = useMemo(() => {
        switch (actionType) {
            case "start":
                return ["primary", i18n.t("screens.contract_signing.start")];
            case "goHome":
                return ["outlined", i18n.t("screens.contract_signing.go_home")];
            case "goMission":
                return ["outlined", i18n.t("screens.contract_signing.back")];
            case "retry":
                return ["primary", i18n.t("screens.contract_signing.reset_signing")];
            case "canceled":
                return ["outlined", i18n.t("screens.contract_signing.postpone_signing")];
            default:
                return ["primary", undefined];
        }
    }, [actionType, i18n]);

    const [buttonModeTwo, buttonTitleTwo]: [ButtonMode?, string?] = useMemo(() => {
        switch (actionType) {
            case "canceled":
                return ["primary", i18n.t("screens.contract_signing.retry_signing")];
            default:
                return [undefined, undefined];
        }
    }, [actionType, i18n]);

    const isRedirect = useMemo(
        () => Platform.OS === "web" && webViewUrl?.startsWith("https://my.skribble.com/view/"),
        [webViewUrl],
    );

    const messageComponent = useMemo(() => {
        switch (actionType) {
            case "canceled":
                return (
                    <BubbleTalk
                        personImageComponent={<Avatar2Svg width="100%" height="100%" />}
                        position="top-center"
                        bubbleBackgroundColor={colors.background}
                    >
                        <TitleMediumText>
                            {i18n.t("screens.contract_signing.state_message.sign_canceled")}
                        </TitleMediumText>
                    </BubbleTalk>
                );
            default:
                return null;
        }
    }, [actionType, colors.background, i18n]);

    useEffect(() => {
        if (isRedirect) {
            navigation.replace("ContractSigningScreen", {
                contractId: route.params.contractId,
                initialState: "canceled",
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.location.href = webViewUrl;
        }
    }, [isRedirect, navigation, route.params.contractId, webViewUrl]);

    useEffect(() => {
        if (actionType === "canceled") {
            navigation.setOptions({ headerShown: false });
        }
    }, [actionType, navigation]);

    useEffect(() => {
        if (asset) {
            onSuccess();
        }
    }, [asset, onSuccess]);

    useStatusBarTranslucent();

    // when the signature is canceled, the waiting for asset is still running
    // but we obviously never receive it.
    // we don't want to display this "waiting" message when user canceled the signing process
    const displayWaiting = waiting && actionType !== "canceled";

    if (isRedirect) {
        return (
            <Loading style={{ height: "100%", alignSelf: "center", justifyContent: "center" }} />
        );
    }
    if (actionType == "retry") {
        return (
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.contract_signing.reset_signing"),
                        mode: "primary",
                        onPress: resetSigning,
                        validating: loading || reseting,
                    },
                ]}
                headband={
                    resetError
                        ? {
                              text: i18n.t("screens.contract_signing.reset_error"),
                              mode: "error",
                          }
                        : undefined
                }
            >
                <VStack gap={1} h="2/3">
                    <Image source={require("../../assets/images/404-image.png")} />
                    <VStack gap={1}>
                        <Button
                            buttonMode="outlined"
                            label={i18n.t("screens.contract_signing.contact_support")}
                            processing={reseting}
                            onPress={() => {
                                void presentIntercom();
                            }}
                        />
                    </VStack>
                </VStack>
            </ScreenCard>
        );
    }
    if (webViewUrl) {
        return (
            <WebViewWrapper
                url={webViewUrl}
                id="signing-frame"
                enableBack
                getDownloadFileName={() => {
                    return i18n.t(
                        `mission.contract_names.${profileType}-${
                            currentContractId.current || "0"
                        }.pdf`,
                    );
                }}
                onUrlChanged={(url) => {
                    // we log this to be able to display the url previously called
                    // as sentry does keep this information when generating a report
                    // eslint-disable-next-line no-console
                    console.log("Signing url changed --> ", `>${route.path}<`, url);
                    if (isSuccessEndUrls(url)) {
                        // onSuccess();
                        setWebviewUrl(null);
                    } else if (isCancelEndUrls(url)) {
                        setWebviewUrl(null);
                        setActionType("canceled");
                    } else if (isFailEndUrls(url)) {
                        // problem in skribble signing process
                        setWebviewUrl(null);
                        setActionType("goMission");
                        setMessageInfo({
                            text: i18n.t("screens.contract_signing.state_message.sign_failed"),
                            mode: "error",
                        });
                    } else if (url.indexOf("/storybook/close-web-view") !== -1) {
                        // case only for storybook
                        setWebviewUrl(null);
                        setMessageInfo({
                            text: "WebView closed in storybook",
                            mode: "info",
                        });
                    }
                }}
            />
        );
    }

    return (
        <ScreenCard
            isContentContainerFlex
            buttons={
                !!buttonTitle &&
                !initializing &&
                !displayWaiting && [
                    ...(buttonTitleTwo
                        ? [
                              {
                                  title: buttonTitleTwo,
                                  mode: buttonModeTwo,
                                  validating: loading,
                                  onPress: onBottomActionTwo,
                              },
                          ]
                        : []),
                    {
                        title: buttonTitle,
                        mode: buttonMode,
                        validating: loading,
                        onPress: onBottomAction,
                    },
                ]
            }
            headband={
                error
                    ? {
                          text: i18n.t("screens.contract_signing.signing_error"),
                          mode: "error",
                      }
                    : undefined
            }
            backgroundGradient={actionType === "canceled" ? kycGradient : undefined}
        >
            {displayWaiting || loading || initializing ? (
                <VStack gap={1.5} style={{ alignItems: "center" }}>
                    <Loading />
                    <Text style={fonts.regular}>
                        {waiting
                            ? i18n.t("contract.signing_status.waiting_contract_asset")
                            : i18n.t("screens.contract_signing.loading")}
                    </Text>
                </VStack>
            ) : (
                <VStack
                    h={1}
                    gap={1}
                    alignment="stretch"
                    style={{
                        flex: 1,
                        justifyContent: actionType === "canceled" ? "center" : undefined,
                    }}
                >
                    {messageComponent}
                    {messageInfo && <MessageInfoDisplay messageInfo={messageInfo} />}

                    {actionType == "start" && (
                        <MarkdownView markdown={i18n.t("screens.contract_signing.description")} />
                    )}

                    {messageInfo?.mode == "error" && (
                        <VStack gap={1}>
                            <Button
                                label={i18n.t("screens.contract_signing.contact_support")}
                                processing={reseting}
                                onPress={() => {
                                    void presentIntercom();
                                }}
                            />
                        </VStack>
                    )}
                </VStack>
            )}
        </ScreenCard>
    );
};

export default ContractSigningScreen;
